import React, { useEffect, useMemo } from "react";
import {
  Checkbox,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { Control, Controller, useWatch } from "react-hook-form";
import { SELECTED_PROPERTY_TRACKS_FORM_KEY } from "@/modules/lead-page/constants";
import useGetLeadPropertyTracks from "@/modules/lead-page/hooks/get-lead-property-tracks";

interface PropertyTrackSelection {
  trackUId: string;
  label: string;
}

interface PropertyTrackUnitFieldProps {
  control: Control;
  setValue: (name: string, value: any) => void;
}

const toSelections = (value: unknown): PropertyTrackSelection[] =>
  Array.isArray(value) ? value : value ? [value as PropertyTrackSelection] : [];

// Property Tracks multi-select rendered on statuses flagged with
// showPropertyTrackUnit (statuses table flag). Options come live from the
// lead's property tracks; the selection is always required. With no tracks
// the dropdown shows a disabled empty state and validation blocks the submit.
const PropertyTrackUnitField: React.FC<PropertyTrackUnitFieldProps> = ({
  control,
  setValue,
}) => {
  const {
    options: leadTrackOptions,
    isLoading: isLoadingLeadTracks,
    isError: isLeadTracksError,
  } = useGetLeadPropertyTracks();

  const trackValue = useWatch({
    control,
    name: SELECTED_PROPERTY_TRACKS_FORM_KEY,
  });

  const selectedTracks = useMemo(() => toSelections(trackValue), [trackValue]);

  // Auto-select the single available track — the selection is required, so a
  // lone option is the only valid submission anyway.
  useEffect(() => {
    if (
      !isLoadingLeadTracks &&
      leadTrackOptions.length === 1 &&
      selectedTracks.length === 0
    ) {
      const only = leadTrackOptions[0];
      setValue(SELECTED_PROPERTY_TRACKS_FORM_KEY, [
        { trackUId: only.trackUId, label: only.label },
      ]);
    }
  }, [isLoadingLeadTracks, leadTrackOptions, selectedTracks.length, setValue]);

  return (
    <Controller
      name={SELECTED_PROPERTY_TRACKS_FORM_KEY}
      control={control}
      defaultValue={[]}
      rules={{
        validate: (value) => {
          if (toSelections(value).length > 0) return true;
          if (isLeadTracksError) {
            return "Property tracks failed to load. Please close and try again.";
          }
          if (isLoadingLeadTracks) {
            return "Property tracks are still loading. Please wait.";
          }
          if (leadTrackOptions.length === 0) {
            return "No property tracks are linked to this lead. Link a property track before applying this status.";
          }
          return "Property track is required";
        },
      }}
      render={({ field, fieldState: { error } }) => {
        const selectedTrackUIds = toSelections(field.value).map(
          (selection) => selection.trackUId
        );

        return (
          <FormControl fullWidth error={!!error}>
            <InputLabel id={`${SELECTED_PROPERTY_TRACKS_FORM_KEY}-track-label`}>
              Property Tracks
            </InputLabel>
            <Select
              multiple
              labelId={`${SELECTED_PROPERTY_TRACKS_FORM_KEY}-track-label`}
              label="Property Tracks"
              data-testid="property-track-unit-track-select"
              value={selectedTrackUIds}
              disabled={isLoadingLeadTracks}
              renderValue={(selected) =>
                (selected as string[])
                  .map(
                    (trackUId) =>
                      leadTrackOptions.find(
                        (option) => option.trackUId === trackUId
                      )?.label || trackUId
                  )
                  .join(", ")
              }
              onChange={(event) => {
                const trackUIds = event.target.value as string[];
                field.onChange(
                  trackUIds
                    .map((trackUId) =>
                      leadTrackOptions.find(
                        (option) => option.trackUId === trackUId
                      )
                    )
                    .filter(Boolean)
                    .map((matched) => ({
                      trackUId: matched!.trackUId,
                      label: matched!.label,
                    }))
                );
              }}
            >
              {leadTrackOptions.length === 0 && (
                <MenuItem value="" disabled>
                  <em>No property tracks linked to this lead</em>
                </MenuItem>
              )}
              {leadTrackOptions.map((option, index) => (
                <MenuItem
                  key={option.trackUId || `track-${index}`}
                  value={option.trackUId}
                >
                  <Checkbox
                    checked={selectedTrackUIds.includes(option.trackUId)}
                    sx={{ padding: "0 8px 0 0" }}
                  />
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {error?.message && <FormHelperText>{error.message}</FormHelperText>}
          </FormControl>
        );
      }}
    />
  );
};

export default PropertyTrackUnitField;
